import { useCallback, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { supabase } from './supabase'
import { getFullName } from './clientUtils'

dayjs.extend(utc)
dayjs.extend(timezone)

// How many recent inbound messages we surface in the notification feed. The
// feed is a glanceable summary, not the full inbox — MessagesManager owns that.
const MESSAGE_LIMIT = 25

// Bucket a timestamp into the design's Today / Yesterday / Earlier groups,
// evaluated in the merchant's own timezone so "today" matches their wall clock.
function bucketFor(ts, tz) {
	const d = dayjs(ts).tz(tz)
	const today = dayjs().tz(tz).startOf('day')
	const days = today.diff(d.startOf('day'), 'day')
	if (days <= 0) return 'today'
	if (days === 1) return 'yesterday'
	return 'earlier'
}

// Row timestamp label: time-of-day for recent items, date for older ones —
// mirrors the seed data in the design (e.g. "9:49 AM" vs "Tue, Jun 9").
function timeLabel(ts, tz, bucket) {
	const d = dayjs(ts).tz(tz)
	return bucket === 'earlier' ? d.format('ddd, MMM D') : d.format('h:mm A')
}

// Collapse a merchant's pending appointments into one notification per booking
// request (grouped by group_id, the way PendingAppointments groups them).
function groupPending(pendingAppointments, tz) {
	const groups = new Map()
	for (const appt of pendingAppointments || []) {
		const key = appt.group_id || appt.id
		if (!groups.has(key)) groups.set(key, [])
		groups.get(key).push(appt)
	}

	const out = []
	for (const [groupId, appts] of groups.entries()) {
		appts.sort((a, b) => new Date(a.start_ts) - new Date(b.start_ts))
		const first = appts[0]
		// Requests carry no created_at on the slot itself reliably across the
		// codebase, so fall back to the appointment start for ordering/bucketing.
		const stamp = first.created_at || first.start_ts
		const bucket = bucketFor(stamp, tz)
		const employee = first.employees
			? `${first.employees.first_name || ''} ${first.employees.last_name || ''}`.trim()
			: 'Next available'
		const slot = dayjs(first.start_ts).tz(tz).format('ddd, MMM D · h:mm A')

		// Per-appointment minutes = base service duration + all its add-on
		// durations. Each appointment carries its own add-ons in the `add_ons`
		// JSONB column ([{ name, price, duration_minutes }]).
		const apptMinutes = (a) =>
			(a.service_types?.duration_minutes || 0) +
			(Array.isArray(a.add_ons) ? a.add_ons.reduce((s, x) => s + (x.duration_minutes || 0), 0) : 0)
		const durTotal = appts.reduce((s, a) => s + apptMinutes(a), 0)

		// One entry per pet in the group (group_id ties a multi-pet request together).
		const pets = appts.map((a) => {
			const mins = apptMinutes(a)
			return {
				name: a.pets?.name || 'Pet',
				service: a.service_types?.name || 'Service',
				duration: mins ? `${mins} min` : '',
				addons: Array.isArray(a.add_ons) ? a.add_ons.map((x) => x.name).filter(Boolean) : [],
			}
		})

		out.push({
			id: `req-${groupId}`,
			groupId,
			appointments: appts,
			firstAppointment: first,
			kind: 'request',
			sub: 'booking',
			bucket,
			stamp,
			status: 'pending',
			unread: true,
			client: first.customer_name || 'Client',
			clientId: first.user_id,
			time: timeLabel(stamp, tz, bucket),
			title: 'Booking request',
			whenDate: dayjs(first.start_ts).tz(tz).format('ddd, MMM D'),
			whenTime: dayjs(first.start_ts).tz(tz).format('h:mm A'),
			groomer: employee,
			total: durTotal ? `${durTotal} min` : '',
			pets,
			approveLabel: appts.length > 1 ? `Approve & book ${appts.length}` : 'Approve & book',
			confirm: `Booked · ${slot}`,
		})
	}
	return out
}

/**
 * Builds the combined notification feed for the topbar bell.
 *
 * Two live sources, matching the design's implemented types:
 *   - Booking requests  → derived from `pendingAppointments` (already fetched
 *     + kept live by NavigationSidebar). Inline Approve / Decline with an
 *     approved/declined + Undo state.
 *   - Client messages   → recent inbound SMS, fetched here and kept live via a
 *     realtime subscription. Unread state reuses the same localStorage-backed
 *     `lastReadMap` that powers the sidebar badge, and opening a message calls
 *     the shared `markRead` so the two stay in sync.
 *
 * Request actions are applied optimistically and overlaid on top of the live
 * pending list so an approved row stays visible (with Undo) even after the
 * realtime update drops it from `pendingAppointments`.
 */
export function useNotificationFeed(merchant, pendingAppointments, { lastReadMap = {}, markRead } = {}) {
	const merchantId = merchant?.id
	const tz = merchant?.timezone || 'UTC'

	const [messages, setMessages] = useState([])
	// Per-request action overlay: { [reqId]: { status, snapshot } }. The snapshot
	// keeps the grouped request data around after it leaves the live pending list.
	const [overrides, setOverrides] = useState({})

	// Fetch recent inbound messages with client names for previews.
	useEffect(() => {
		if (!merchantId) return undefined
		let cancelled = false
		;(async () => {
			const { data, error } = await supabase
				.from('sms_messages')
				.select('id, client_id, message_text, created_at, clients(name, last_name)')
				.eq('merchant_id', merchantId)
				.eq('direction', 'inbound')
				.order('created_at', { ascending: false })
				.limit(MESSAGE_LIMIT)
			if (cancelled) return
			if (error) {
				console.error('useNotificationFeed: message fetch failed', error)
				return
			}
			setMessages(data || [])
		})()
		return () => {
			cancelled = true
		}
	}, [merchantId])

	// Keep the message feed live. Prepend inbound inserts; cap at MESSAGE_LIMIT.
	useEffect(() => {
		if (!merchantId) return undefined
		const channel = supabase
			.channel(`notif-feed-sms:${merchantId}`)
			.on(
				'postgres_changes',
				{ event: 'INSERT', schema: 'public', table: 'sms_messages', filter: `merchant_id=eq.${merchantId}` },
				async (payload) => {
					const msg = payload.new
					if (!msg || msg.direction !== 'inbound') return
					// The realtime payload has no joined client; look the name up so
					// the preview row reads "<Client Name>" like the fetched rows.
					let clients = null
					try {
						const { data } = await supabase
							.from('clients')
							.select('name, last_name')
							.eq('id', msg.client_id)
							.single()
						clients = data
					} catch {
						/* fall back to no name */
					}
					setMessages((prev) => {
						if (prev.some((m) => m.id === msg.id)) return prev
						return [{ ...msg, clients }, ...prev].slice(0, MESSAGE_LIMIT)
					})
				}
			)
			.subscribe()
		return () => {
			supabase.removeChannel(channel)
		}
	}, [merchantId])

	// ── request actions ────────────────────────────────────────────────
	const approve = useCallback(
		async (req) => {
			setOverrides((o) => ({ ...o, [req.id]: { status: 'approved', snapshot: req } }))
			try {
				const ids = req.appointments.map((a) => a.id)
				const { error } = await supabase.from('appointments').update({ status: 'booked' }).in('id', ids)
				if (error) throw error
			} catch (e) {
				console.error('useNotificationFeed: approve failed', e)
				setOverrides((o) => ({ ...o, [req.id]: { status: 'pending', snapshot: req } }))
			}
		},
		[]
	)

	const decline = useCallback(
		async (req) => {
			setOverrides((o) => ({ ...o, [req.id]: { status: 'declined', snapshot: req } }))
			try {
				const { data, error } = await supabase.functions.invoke('cancel-appointment', {
					body: { appointmentId: req.firstAppointment.id },
				})
				if (error) throw error
				if (data && data.success === false) throw new Error(data.error || 'Failed to cancel')
			} catch (e) {
				console.error('useNotificationFeed: decline failed', e)
				setOverrides((o) => ({ ...o, [req.id]: { status: 'pending', snapshot: req } }))
			}
		},
		[]
	)

	const openMessage = useCallback(
		(clientId) => {
			if (clientId && markRead) markRead(clientId)
		},
		[markRead]
	)

	// Only messages have a read state we can clear in bulk — booking requests
	// clear by being approved/declined, not by "mark read".
	const markMessagesRead = useCallback(() => {
		const seen = new Set()
		for (const m of messages) {
			if (m.client_id && !seen.has(m.client_id)) {
				seen.add(m.client_id)
				markRead && markRead(m.client_id)
			}
		}
	}, [messages, markRead])

	// ── assemble the feed ───────────────────────────────────────────────
	const notifications = useMemo(() => {
		const requests = groupPending(pendingAppointments, tz)
		const liveIds = new Set(requests.map((r) => r.id))

		// Apply optimistic overrides to live requests, and re-add any acted
		// request that already left the live pending list (so its approved /
		// declined confirmation stays visible after the realtime update drops it).
		const requestRows = requests.map((r) => {
			const ov = overrides[r.id]
			return ov ? { ...r, status: ov.status, unread: false } : r
		})
		for (const [id, ov] of Object.entries(overrides)) {
			if (!liveIds.has(id) && ov.status !== 'pending' && ov.snapshot) {
				requestRows.push({ ...ov.snapshot, status: ov.status, unread: false })
			}
		}

		const messageRows = messages.map((m) => {
			const readAt = lastReadMap[m.client_id]
			const unread = !readAt || new Date(m.created_at) > new Date(readAt)
			const bucket = bucketFor(m.created_at, tz)
			return {
				id: `msg-${m.id}`,
				kind: 'message',
				clientId: m.client_id,
				bucket,
				stamp: m.created_at,
				unread,
				client: getFullName(m.clients) || 'Client',
				pet: '',
				time: timeLabel(m.created_at, tz, bucket),
				title: 'New message',
				preview: m.message_text || '',
			}
		})

		// Newest first within the merged feed; bucketing into Today/Yesterday/
		// Earlier happens in the panel.
		return [...requestRows, ...messageRows].sort((a, b) => new Date(b.stamp) - new Date(a.stamp))
	}, [pendingAppointments, messages, overrides, lastReadMap, tz])

	const unreadCount = useMemo(
		() => notifications.filter((n) => n.unread).length,
		[notifications]
	)

	// Unread messages specifically — drives the "Mark messages read" action,
	// which can't touch booking requests.
	const unreadMessageCount = useMemo(
		() => notifications.filter((n) => n.kind === 'message' && n.unread).length,
		[notifications]
	)

	return { notifications, unreadCount, unreadMessageCount, approve, decline, openMessage, markMessagesRead }
}
